import React, { useState } from 'react'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  Paper,
  Stepper,
  Step,
  StepLabel
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  NavigateNext as NavigateNextIcon,
  SkipNext as SkipNextIcon
} from '@mui/icons-material'
import toast from 'react-hot-toast'

const GhostTutorial = ({ userData, onBack }) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [open, setOpen] = useState(true)

  const tutorialSteps = [
    {
      title: "Hoşgeldin! 👻",
      content: "Benim adım Ghost! Sana başlangıç için yol arkadaşlığı yapacağım. Hazır mısın?",
      highlight: null
    },
    {
      title: "Genel Bakış",
      content: "Bu kartlar ile öğrenci işlemlerini yönetebilir, öğretmenlerinle iletişim kurabilir ve eğitim sürecini takip edebilirsin.",
      highlight: null
    },
    {
      title: "🚪 Sınıflarım Kartı",
      content: "Sınıflara katılabilir, katıldığın sınıfları görüntüleyebilir, sınıf detaylarını inceleyebilirsin.",
      highlight: 'classes'
    },
    {
      title: "👨‍🏫 Öğretmenlerim Kartı",
      content: "Öğretmenlerini görüntüleyebilir ve onlara soru gönderebilirsin.",
      highlight: 'teachers'
    },
    {
      title: "📝 Görevler Kartı",
      content: "Öğretmenlerinin gönderdiği görevleri görüntüleyebilir ve tamamlayabilirsin.",
      highlight: 'tasks'
    },
    {
      title: "📅 Ders Programı",
      content: "Ders programını yönetebilirsin. Günlük bildirimler alabilir kendine not bırakabilirsin.",
      highlight: 'schedule'
    },
    {
      title: "📄 Gelen Cevaplar",
      content: "Öğretmenlerinden gelen cevapları görüntüleyebilirsin.",
      highlight: 'answers'
    },
    {
      title: "📚 Gelen Dökümanlar",
      content: "Öğretmenlerinden gelen dökümanları görüntüleyebilirsin.",
      highlight: 'documents'
    },
    {
      title: "🌐 Çeviri",
      content: "Hızlı çeviri yapabilir ve bu çevirileri hafızalayabilirsin.",
      highlight: 'translate'
    },
    {
      title: "🧠 Hafıza",
      content: "Hafızaladığın bilgilere buradan ulaşabilir günlük bildirimler alabilirsin.",
      highlight: 'memory'
    },
    {
      title: "Tebrikler! 🎉",
      content: "Şimdi eğitim butonuna tıklayarak eğitim panelini keşfetmeye başlayabilirsin! Orada benim görselime tıklayarak detaylı bilgi edinebilirsin.",
      highlight: null
    }
  ]

  const handleNext = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1)
    } else {
      handleClose()
    }
  }

  const handleSkip = () => {
    handleClose()
  }

  const handleClose = () => {
    setOpen(false)
    onBack()
  }

  const getHighlightedCardStyle = (cardId) => {
    if (tutorialSteps[currentStep].highlight === cardId) {
      return {
        transform: 'scale(1.05)',
        boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
        border: '3px solid #1976d2',
        transition: 'all 0.3s ease-in-out'
      }
    }
    return {
      opacity: tutorialSteps[currentStep].highlight ? 0.3 : 1,
      transition: 'all 0.3s ease-in-out'
    }
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Ghost Tutorial 👻
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 3 }}>
        {/* Header Card */}
        <Card sx={{ 
          mx: 2, 
          mt: 2, 
          borderRadius: 3,
          boxShadow: 1,
          backgroundColor: '#f8f9fa'
        }}>
          <CardContent sx={{ p: 2.5 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center">
                <Typography variant="h6" sx={{ mr: 2 }}>👻</Typography>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333' }}>
                    {userData?.username || 'Öğrenci'}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Öğrenci
                  </Typography>
                </Box>
              </Box>
              
              <IconButton
                onClick={() => setOpen(true)}
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: '#1976d2',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#1565c0'
                  }
                }}
              >
                <Typography variant="h6">👻</Typography>
              </IconButton>
            </Box>
          </CardContent>
        </Card>

        {/* Banner Ad Placeholder */}
        <Box sx={{ 
          mx: 2, 
          mt: 1.5, 
          height: 60, 
          backgroundColor: '#f0f0f0', 
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#666'
        }}>
          📢 Reklam Alanı
        </Box>

        {/* Menu Items */}
        <Box sx={{ px: 0 }}>
          <Typography variant="h6" sx={{ 
            px: 2, 
            mb: 2, 
            color: '#6E6D6D',
            fontWeight: 'bold',
            fontSize: '20px'
          }}>
            Öğrenci İşlemleri
          </Typography>
          
          {/* Sınıflarım */}
          <Card sx={{ 
            mx: 2, 
            mb: 1, 
            borderRadius: 2,
            ...getHighlightedCardStyle('classes')
          }}>
            <CardContent sx={{ p: 2 }}>
              <Box display="flex" alignItems="center">
                <Typography variant="h6" sx={{ mr: 2, color: '#1976d2' }}>🚪</Typography>
                <Typography variant="h6" fontWeight="bold" color="#333">
                  Sınıflarım
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Öğretmenlerim */}
          <Card sx={{ 
            mx: 2, 
            mb: 1, 
            borderRadius: 2,
            ...getHighlightedCardStyle('teachers')
          }}>
            <CardContent sx={{ p: 2 }}>
              <Box display="flex" alignItems="center">
                <Typography variant="h6" sx={{ mr: 2, color: '#9c27b0' }}>👨‍🏫</Typography>
                <Typography variant="h6" fontWeight="bold" color="#333">
                  Öğretmenlerim
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Görevler */}
          <Card sx={{ 
            mx: 2, 
            mb: 1, 
            borderRadius: 2,
            ...getHighlightedCardStyle('tasks')
          }}>
            <CardContent sx={{ p: 2 }}>
              <Box display="flex" alignItems="center">
                <Typography variant="h6" sx={{ mr: 2, color: '#ed6c02' }}>📝</Typography>
                <Typography variant="h6" fontWeight="bold" color="#333">
                  Görevler
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Ders Programı */}
          <Card sx={{ 
            mx: 2, 
            mb: 1, 
            borderRadius: 2,
            ...getHighlightedCardStyle('schedule')
          }}>
            <CardContent sx={{ p: 2 }}>
              <Box display="flex" alignItems="center">
                <Typography variant="h6" sx={{ mr: 2, color: '#1976d2' }}>📅</Typography>
                <Typography variant="h6" fontWeight="bold" color="#333">
                  Ders Programı
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Gelen Yanıtlar */}
          <Card sx={{ 
            mx: 2, 
            mb: 1, 
            borderRadius: 2,
            ...getHighlightedCardStyle('answers')
          }}>
            <CardContent sx={{ p: 2 }}>
              <Box display="flex" alignItems="center">
                <Typography variant="h6" sx={{ mr: 2, color: '#2e7d32' }}>📄</Typography>
                <Typography variant="h6" fontWeight="bold" color="#333">
                  Gelen Yanıtlar
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Gelen Dökümanlar */}
          <Card sx={{ 
            mx: 2, 
            mb: 1, 
            borderRadius: 2,
            ...getHighlightedCardStyle('documents')
          }}>
            <CardContent sx={{ p: 2 }}>
              <Box display="flex" alignItems="center">
                <Typography variant="h6" sx={{ mr: 2, color: '#0288d1' }}>📚</Typography>
                <Typography variant="h6" fontWeight="bold" color="#333">
                  Gelen Dökümanlar
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Çeviri */}
          <Card sx={{ 
            mx: 2, 
            mb: 1, 
            borderRadius: 2,
            ...getHighlightedCardStyle('translate')
          }}>
            <CardContent sx={{ p: 2 }}>
              <Box display="flex" alignItems="center">
                <Typography variant="h6" sx={{ mr: 2, color: '#9c27b0' }}>🌐</Typography>
                <Typography variant="h6" fontWeight="bold" color="#333">
                  Çeviri
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Hafıza */}
          <Card sx={{ 
            mx: 2, 
            mb: 1, 
            borderRadius: 2,
            ...getHighlightedCardStyle('memory')
          }}>
            <CardContent sx={{ p: 2 }}>
              <Box display="flex" alignItems="center">
                <Typography variant="h6" sx={{ mr: 2, color: '#2e7d32' }}>🧠</Typography>
                <Typography variant="h6" fontWeight="bold" color="#333">
                  Hafıza
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Container>

      {/* Tutorial Dialog */}
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }
        }}
      >
        <DialogContent sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold' }}>
            {tutorialSteps[currentStep].title}
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.6 }}>
            {tutorialSteps[currentStep].content}
          </Typography>

          {/* Progress Stepper */}
          <Stepper activeStep={currentStep} sx={{ mb: 2 }}>
            {tutorialSteps.map((step, index) => (
              <Step key={index}>
                <StepLabel sx={{ 
                  '& .MuiStepLabel-label': { 
                    color: 'white',
                    fontSize: '0.8rem'
                  }
                }} />
              </Step>
            ))}
          </Stepper>
        </DialogContent>

        <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
          <Button
            onClick={handleSkip}
            startIcon={<SkipNextIcon />}
            sx={{ 
              color: 'white',
              border: '1px solid white',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            Atlayalım
          </Button>
          
          <Button
            onClick={handleNext}
            endIcon={currentStep === tutorialSteps.length - 1 ? <CloseIcon /> : <NavigateNextIcon />}
            variant="contained"
            sx={{ 
              backgroundColor: 'white',
              color: '#667eea',
              '&:hover': {
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            {currentStep === tutorialSteps.length - 1 ? 'Bitir' : 'İleri'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default GhostTutorial
